import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { articleService, reviewerService, assignmentService } from '../services/api';

const eligible = ['Submitted', 'Under Review', 'UnderReview'];
const labels = { Active: 'فعال', Withdrawn: 'لغوشده', Reassigned: 'منتقل‌شده' };

export default function AdminAssignments() {
  const session = useAdminPermissions();
  const allowed = session.can('articles.view') && session.can('reviewers.view');
  const [articles, setArticles] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [articleId, setArticleId] = useState('');
  const [reviewerId, setReviewerId] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const fail = error => {
    if (error.response?.status === 401) setExpired(true);
    setError(error.response?.data?.message || (error.response?.status === 403 ? 'اجازه انجام این عملیات را ندارید.' : 'دریافت یا ذخیره اطلاعات ناموفق بود. صفحه را تازه‌سازی کنید.'));
  };
  useEffect(() => {
    if (!allowed) return;
    let active = true;
    Promise.all([articleService.getForManagement(), reviewerService.getAll()]).then(([a, r]) => {
      if (active) { setArticles(a.data); setReviewers(r.data); }
    }).catch(error => { if (active) fail(error); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [allowed]);
  useEffect(() => {
    setAssignments([]); setEditing(null); setReason(''); setReviewerId('');
    if (!articleId) return;
    let active = true; setLoading(true);
    assignmentService.list(articleId).then(({ data }) => { if (active) setAssignments(data); })
      .catch(error => { if (active) fail(error); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [articleId]);

  const mutate = async (kind, assignment = null) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const data = { articleId: Number(articleId), reviewerId: Number(reviewerId), reason };
      if (kind === 'assign') await assignmentService.assign(data);
      if (kind === 'reassign') await assignmentService.reassign(assignment.id, data);
      if (kind === 'withdraw') await assignmentService.withdraw(assignment.id, data);
      setAssignments((await assignmentService.list(articleId)).data);
      setEditing(null); setReason(''); setReviewerId(''); setNotice('تخصیص‌ها به‌روزرسانی شدند.');
    } catch (error) { fail(error); }
    finally { setBusy(false); }
  };
  if (session.loading) return <p role="status" className="p-8">در حال بررسی دسترسی…</p>;
  if (session.status === 401 || expired) return <Navigate to="/admin-login" replace />;
  if (!allowed) return <p role="alert" className="p-8">اجازه مشاهده تخصیص‌ها را ندارید.</p>;
  const article = articles.find(a => a.id === Number(articleId));
  const canAssign = session.can('articles.update') && eligible.includes(article?.status);
  const candidates = reviewers.filter(r => r.isActive && r.hasAccount && !assignments.some(a => a.status === 'Active' && a.reviewerId === r.id));
  return <div dir="rtl" className="min-h-screen bg-gray-100"><AdminNav />
    <div className="max-w-5xl mx-auto p-4 sm:p-8 space-y-4">
      <h1 className="text-2xl font-bold">تخصیص داور به مقاله</h1>
      <p>تخصیص فقط برای مقاله ارسال‌شده یا در حال بررسی و داور فعال با حساب متصل انجام می‌شود.</p>
      <Link className="text-blue-700" to="/admin/reviewers">مدیریت داوران و اتصال حساب</Link>
      {error && <p role="alert" className="bg-red-50 text-red-800 p-3">{error}</p>}
      {notice && <p role="status" className="text-green-800">{notice}</p>}
      <label className="block">مقاله
        <select aria-label="مقاله" disabled={busy || loading} className="border rounded p-2 block w-full" value={articleId} onChange={event => { setArticleId(event.target.value); setError(''); setNotice(''); }}>
          <option value="">انتخاب مقاله</option>
          {articles.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
        </select>
      </label>
      {loading && <p role="status">در حال دریافت اطلاعات…</p>}
      {articleId && !loading && <>
        {!eligible.includes(article?.status) && <p>وضعیت فعلی مقاله برای تخصیص یا انتقال مجاز نیست. لغو تخصیص فعال همچنان ممکن است.</p>}
        {canAssign && editing === null && <form className="bg-white p-4 rounded space-y-3" onSubmit={event => { event.preventDefault(); mutate('assign'); }}>
          <label className="block">داور
            <select aria-label="داور" required disabled={busy} value={reviewerId} onChange={event => setReviewerId(event.target.value)} className="border p-2 block w-full">
              <option value="">انتخاب داور</option>{candidates.map(r => <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>)}
            </select>
          </label>
          {candidates.length === 0 && <p>داور واجد شرایط دیگری موجود نیست.</p>}
          <button disabled={busy || !reviewerId} className="bg-blue-700 text-white px-4 py-2 rounded">تخصیص داور</button>
        </form>}
        {assignments.length === 0 && <p>هنوز تخصیصی برای این مقاله ثبت نشده است.</p>}
        <ul className="space-y-3">{assignments.map(a => <li key={a.id} className="bg-white rounded p-4 break-words">
          <h2 className="font-bold">{a.reviewerName}</h2><p>وضعیت: {labels[a.status]}</p>
          <p>تاریخ تخصیص: {new Date(a.assignedAt).toLocaleString('fa-IR')}</p>
          {a.previousAssignmentId && <p>جایگزین تخصیص شماره {a.previousAssignmentId}</p>}
          {a.reason && <p>دلیل: {a.reason}</p>}
          {a.closedAt && <p>تاریخ پایان: {new Date(a.closedAt).toLocaleString('fa-IR')}</p>}
          {a.status === 'Active' && session.can('articles.update') && <div className="mt-3 flex gap-4">
            {canAssign && <button disabled={busy} className="text-blue-700" onClick={() => { setEditing({ id: a.id, kind: 'reassign' }); setReason(''); setReviewerId(''); }}>انتقال به داور دیگر</button>}
            <button disabled={busy} className="text-red-700" onClick={() => { setEditing({ id: a.id, kind: 'withdraw' }); setReason(''); }}>لغو تخصیص</button>
          </div>}
          {editing?.id === a.id && <form className="space-y-3 mt-4" onSubmit={event => { event.preventDefault(); mutate(editing.kind, a); }}>
            {editing.kind === 'reassign' && <label className="block">داور جایگزین<select aria-label="داور جایگزین" required disabled={busy} className="border p-2 block w-full" value={reviewerId} onChange={event => setReviewerId(event.target.value)}>
              <option value="">انتخاب داور</option>{candidates.map(r => <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>)}
            </select></label>}
            <label className="block">دلیل<textarea aria-label="دلیل" required maxLength={1000} disabled={busy} value={reason} onChange={event => setReason(event.target.value)} className="border block w-full p-2" /></label>
            <button disabled={busy} className="bg-blue-700 text-white rounded px-4 py-2">ثبت تغییر</button>
            <button type="button" disabled={busy} className="mr-3" onClick={() => setEditing(null)}>انصراف</button>
          </form>}
        </li>)}</ul>
      </>}
    </div>
  </div>;
}
